/** Rebind one existing MCP tool without rebuilding its authoring source. */
import { createHash } from "node:crypto";
import { isAlias, isMap, isScalar, parseDocument, visit } from "yaml";
import { z } from "zod";
import type { DataverseApi } from "../api/dataverse-api.js";
import { CopilotConnectionReferenceUpdateError } from "../api/dataverse-api.js";
import type { ConnectorMetadataApi } from "../api/connector-metadata.js";
import { ValidationError } from "../utils/errors.js";
import { GUID_PATTERN, validateEnvironmentId } from "../utils/security.js";
import { lintComponent } from "./copilot-studio-tools.js";

const ID_PATTERN = /^[A-Za-z0-9_-]{1,300}$/;
const HASH_PATTERN = /^[a-fA-F0-9]{64}$/;
const MAX_SOURCE_BYTES = 256 * 1024;
const COMPONENT_SELECT = "botcomponentid,_parentbotid_value,componenttype,data";
const LOGICAL_NAME_PATTERN = /^[A-Za-z0-9_.-]{1,400}$/;
const REFERENCE_SELECT =
  "connectionreferenceid,connectionreferencelogicalname,connectorid,connectionid,statecode";
const RELATIONSHIP = "botcomponent_connectionreference";
const LINK_EXPAND = `${RELATIONSHIP}($select=${REFERENCE_SELECT};$top=2)`;
const CONSUMER_EXPAND = `${RELATIONSHIP}($select=botcomponentid,_parentbotid_value,componenttype;$top=2)`;

export const rebindCopilotAgentToolConnectionInputSchema = z
  .object({
    agentId: z
      .string()
      .regex(GUID_PATTERN)
      .describe("Existing agent GUID in the current environment"),
    componentId: z
      .string()
      .regex(GUID_PATTERN)
      .describe("Existing MCP tool component GUID belonging to agentId"),
    connectionId: z
      .string()
      .regex(ID_PATTERN)
      .describe("Exact replacement connection name, not a display name or URL"),
    expectedSourceSha256: z
      .string()
      .regex(HASH_PATTERN)
      .optional()
      .describe("SHA-256 of the exact current YAML source; required with confirm=true"),
    expectedConnectionReferenceSha256: z
      .string()
      .regex(HASH_PATTERN)
      .optional()
      .describe(
        "SHA-256 of selected reference metadata returned by preview; required with confirm=true",
      ),
    confirm: z
      .boolean()
      .default(false)
      .describe(
        "False previews only; true changes the existing reference row's physical connection ID, never YAML",
      ),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.confirm && value.expectedSourceSha256 === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["expectedSourceSha256"],
        message: "Required when confirm=true; preview the current source first.",
      });
    }
    if (value.confirm && value.expectedConnectionReferenceSha256 === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["expectedConnectionReferenceSha256"],
        message: "Required when confirm=true; preview the reference metadata first.",
      });
    }
  });

export const rebindCopilotAgentToolConnectionTool = {
  name: "rebind_copilot_agent_tool_connection",
  description:
    "Preview or rebind one existing Copilot Studio MCP tool by updating ONLY its existing linked Dataverse connectionreference row's physical connectionid. The YAML connectionReference is a logical name, not a physical connection ID; all source bytes stay unchanged. Requires explicit Invoker mode, the same connector and Connected replacement metadata, not runtime proof. Missing/dangling/ambiguous references, inactive rows, other visible component consumers or incomplete relationship reads are refused. confirm=true requires expectedSourceSha256 and expectedConnectionReferenceSha256 from preview; fresh preflight and the reference row ETag guard one conditional PATCH with independent readback and no retry. This is not an atomic source-and-reference transaction or a global consumer audit. Never creates reference rows or links, reauthorizes, changes permissions, binds evaluation users, publishes or executes.",
  inputSchema: {
    type: "object" as const,
    properties: {
      agentId: {
        type: "string",
        pattern: GUID_PATTERN.source,
        description: "Existing agent GUID in the current environment",
      },
      componentId: {
        type: "string",
        pattern: GUID_PATTERN.source,
        description: "Existing MCP tool component GUID belonging to agentId",
      },
      connectionId: {
        type: "string",
        pattern: ID_PATTERN.source,
        description: "Exact replacement connection name, not a display name or URL",
      },
      expectedSourceSha256: {
        type: "string",
        pattern: HASH_PATTERN.source,
        description: "Exact source hash returned by preview; required when confirm=true",
      },
      expectedConnectionReferenceSha256: {
        type: "string",
        pattern: HASH_PATTERN.source,
        description:
          "Exact selected-reference metadata hash returned by preview; required when confirm=true",
      },
      confirm: {
        type: "boolean",
        default: false,
        description:
          "Explicitly update the existing reference row's physical binding; no YAML change",
      },
    },
    required: ["agentId", "componentId", "connectionId"],
    additionalProperties: false,
    allOf: [
      {
        if: { properties: { confirm: { const: true } }, required: ["confirm"] },
        then: { required: ["expectedSourceSha256", "expectedConnectionReferenceSha256"] },
      },
    ],
  },
};

function sourceHash(source: string): string {
  return createHash("sha256").update(source, "utf8").digest("hex");
}

function record(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

/** Normalize only documented provider IDs or bare names, never substrings/URLs. */
function connectorName(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const match = /^\/providers\/Microsoft\.PowerApps\/apis\/([A-Za-z0-9_-]{1,300})$/i.exec(value);
  const name = match?.[1] ?? value;
  return ID_PATTERN.test(name) ? name.toLowerCase() : undefined;
}

function checkedSource(row: Record<string, unknown>, agentId: string, componentId: string): string {
  if (
    typeof row.botcomponentid !== "string" ||
    row.botcomponentid.toLowerCase() !== componentId.toLowerCase()
  ) {
    throw new ValidationError(
      "The returned component identity could not be verified; no rebind was attempted.",
    );
  }
  if (
    typeof row._parentbotid_value !== "string" ||
    row._parentbotid_value.toLowerCase() !== agentId.toLowerCase()
  ) {
    throw new ValidationError(
      "The component does not have the requested agent as its verified parent; no rebind was attempted.",
    );
  }
  if (
    row.componenttype !== 9 ||
    typeof row.data !== "string" ||
    row.data.length === 0 ||
    Buffer.byteLength(row.data, "utf8") > MAX_SOURCE_BYTES
  ) {
    throw new ValidationError(
      "Rebinding requires a type-9 tool component with nonempty YAML source no larger than 256 KiB.",
    );
  }
  return row.data;
}

function checkedTool(source: string) {
  const document = parseDocument(source, { uniqueKeys: true, strict: true, prettyErrors: false });
  if (document.errors.length > 0 || document.warnings.length > 0 || !isMap(document.contents)) {
    throw new ValidationError(
      "The tool source is not unambiguous valid YAML; no rebind was attempted.",
    );
  }
  let nodes = 0;
  visit(document, (_key, node, path) => {
    nodes++;
    if (
      nodes > 20000 ||
      path.length > 64 ||
      isAlias(node) ||
      (node && typeof node === "object" && "tag" in node && node.tag !== undefined) ||
      (node && typeof node === "object" && "anchor" in node && node.anchor !== undefined)
    ) {
      throw new ValidationError(
        "Tool YAML aliases, anchors, explicit tags, or excessive nesting/size are not supported for a safe rebind.",
      );
    }
    if (isMap(node)) {
      for (const pair of node.items) {
        if (
          !isScalar(pair.key) ||
          typeof pair.key.value !== "string" ||
          ["__proto__", "prototype", "constructor", "<<"].includes(pair.key.value)
        ) {
          throw new ValidationError(
            "The tool source contains an ambiguous or unsupported YAML mapping key.",
          );
        }
      }
    }
  });
  const value: unknown = document.toJS({ maxAliasCount: 0 });
  const root = record(value);
  const action = record(root?.action);
  const properties = record(action?.connectionProperties);
  const operation = record(action?.operationDetails);
  if (
    root?.kind !== "TaskDialog" ||
    action?.kind !== "InvokeExternalAgentTaskAction" ||
    operation?.kind !== "ModelContextProtocolMetadata" ||
    typeof operation.operationId !== "string" ||
    !ID_PATTERN.test(operation.operationId)
  ) {
    throw new ValidationError(
      "Only a TaskDialog with one connector-backed InvokeExternalAgentTaskAction and ModelContextProtocolMetadata operation is supported.",
    );
  }
  if (properties?.mode !== "Invoker") {
    throw new ValidationError(
      "Rebinding requires existing explicit Invoker mode; Maker, missing, or ambiguous modes are refused. This tool never changes whose credentials run the tool.",
    );
  }
  let references = 0;
  let actions = 0;
  const scan = (entry: unknown): void => {
    if (Array.isArray(entry)) {
      entry.forEach(scan);
      return;
    }
    const object = record(entry);
    if (!object) return;
    if (Object.hasOwn(object, "connectionReference")) references++;
    if (typeof object.kind === "string" && /^Invoke.*Action$/.test(object.kind)) actions++;
    Object.values(object).forEach(scan);
  };
  scan(value);
  if (references !== 1 || actions !== 1) {
    throw new ValidationError(
      "The component contains multiple or ambiguous tool actions/connection references; no rebind was attempted.",
    );
  }
  const reference =
    typeof action.connectionReference === "string" ? action.connectionReference : undefined;
  const node = document.getIn(["action", "connectionReference"], true);
  if (
    reference === undefined ||
    !LOGICAL_NAME_PATTERN.test(reference) ||
    !isScalar(node) ||
    !node.range
  ) {
    throw new ValidationError(
      "The existing connection reference logical name is invalid; no rebind was attempted.",
    );
  }
  if (lintComponent(9, source).length !== 0) {
    throw new ValidationError(
      "The current tool source failed component lint; resolve it before rebinding.",
    );
  }
  return { reference };
}

function completeRelationship(row: Record<string, unknown>): Record<string, unknown>[] {
  const values = row[RELATIONSHIP];
  if (
    Object.keys(row).some((key) => key.toLowerCase().includes("nextlink")) ||
    !Array.isArray(values) ||
    values.length !== 1 ||
    !record(values[0])
  ) {
    throw new ValidationError(
      "Exactly one complete linked-reference/consumer relationship is required; missing, shared, ambiguous or truncated metadata is refused. No rebind was attempted.",
    );
  }
  return values as Record<string, unknown>[];
}

function checkedReference(row: Record<string, unknown>, logicalName: string) {
  if (
    typeof row.connectionreferenceid !== "string" ||
    !GUID_PATTERN.test(row.connectionreferenceid) ||
    row.connectionreferencelogicalname !== logicalName ||
    typeof row.connectorid !== "string" ||
    !connectorName(row.connectorid) ||
    typeof row.connectionid !== "string" ||
    !ID_PATTERN.test(row.connectionid) ||
    row.statecode !== 0
  ) {
    throw new ValidationError(
      "The existing linked connection reference is missing, inactive, dangling or has unverifiable identity/binding fields; no rebind was attempted.",
    );
  }
  // Fixed-order selected metadata only. ETags and unknown service properties are not hashed.
  const metadata = {
    connectionreferenceid: row.connectionreferenceid,
    connectionreferencelogicalname: logicalName,
    connectorid: row.connectorid,
    connectionid: row.connectionid,
    statecode: 0,
  };
  return { metadata, hash: sourceHash(JSON.stringify(metadata)), etag: row["@odata.etag"] };
}

async function readRow(
  api: DataverseApi,
  entity: string,
  id: string,
  select: string,
  expand?: string,
) {
  try {
    return await api.getRow(entity, id, select, expand);
  } catch (error) {
    if (error instanceof CopilotConnectionReferenceUpdateError) throw error;
    const status = record(error)?.statusCode;
    throw new CopilotConnectionReferenceUpdateError(
      "preflight",
      typeof status === "number" ? status : undefined,
    );
  }
}

async function readBinding(
  api: DataverseApi,
  component: Record<string, unknown>,
  logicalName: string,
  agentId: string,
  componentId: string,
) {
  const linked = checkedReference(completeRelationship(component)[0]!, logicalName);
  const row = await readRow(
    api,
    "connectionreferences",
    linked.metadata.connectionreferenceid,
    REFERENCE_SELECT,
    CONSUMER_EXPAND,
  );
  const reference = checkedReference(row, logicalName);
  if (reference.hash !== linked.hash) {
    throw new ValidationError(
      "The linked reference metadata changed during verification; no rebind was attempted.",
    );
  }
  const consumer = completeRelationship(row)[0]!;
  if (
    typeof consumer.botcomponentid !== "string" ||
    consumer.botcomponentid.toLowerCase() !== componentId.toLowerCase() ||
    typeof consumer._parentbotid_value !== "string" ||
    consumer._parentbotid_value.toLowerCase() !== agentId.toLowerCase() ||
    consumer.componenttype !== 9
  ) {
    throw new ValidationError(
      "The connection reference has another or unverifiable component consumer; shared reference updates are refused. No rebind was attempted.",
    );
  }
  return reference;
}

export async function handleRebindCopilotAgentToolConnection(
  context: {
    api: DataverseApi | null;
    connectorApi: ConnectorMetadataApi | null;
    environmentId: string;
  },
  input: unknown,
): Promise<string> {
  const parsed = rebindCopilotAgentToolConnectionInputSchema.parse(input);
  const { api, connectorApi } = context;
  if (!api || !connectorApi) {
    throw new ValidationError(
      "Dataverse and connector APIs must both be configured for the current environment before rebinding.",
    );
  }
  const environmentId = validateEnvironmentId(context.environmentId);
  const agent = await readRow(api, "bots", parsed.agentId, "botid,schemaname");
  if (
    typeof agent.botid !== "string" ||
    agent.botid.toLowerCase() !== parsed.agentId.toLowerCase() ||
    typeof agent.schemaname !== "string" ||
    !ID_PATTERN.test(agent.schemaname)
  ) {
    throw new ValidationError(
      "The requested agent identity/schema could not be verified; no rebind was attempted.",
    );
  }
  const row = await readRow(
    api,
    "botcomponents",
    parsed.componentId,
    COMPONENT_SELECT,
    LINK_EXPAND,
  );
  const source = checkedSource(row, parsed.agentId, parsed.componentId);
  const beforeSha256 = sourceHash(source);
  if (
    parsed.expectedSourceSha256 !== undefined &&
    parsed.expectedSourceSha256.toLowerCase() !== beforeSha256
  ) {
    throw new ValidationError(
      "The component source changed since the supplied hash. Preview again; no rebind was attempted.",
    );
  }
  const tool = checkedTool(source);
  const reference = await readBinding(api, row, tool.reference, parsed.agentId, parsed.componentId);
  if (
    parsed.expectedConnectionReferenceSha256 !== undefined &&
    parsed.expectedConnectionReferenceSha256.toLowerCase() !== reference.hash
  ) {
    throw new ValidationError(
      "The connection reference changed since the supplied hash. Preview again; no rebind was attempted.",
    );
  }
  let connections;
  try {
    connections = await connectorApi.listConnections(environmentId);
  } catch {
    throw new ValidationError(
      "Connection visibility/status lookup failed in the current environment; no rebind was attempted. Resolve that lookup before retrying.",
    );
  }
  if (!Array.isArray(connections) || connections.length > 10000) {
    throw new ValidationError(
      "The connection lookup returned an unsupported or oversized result; no rebind was attempted.",
    );
  }
  const matches = connections.filter(
    (connection) =>
      typeof connection?.name === "string" &&
      connection.name.toLowerCase() === parsed.connectionId.toLowerCase(),
  );
  if (matches.length !== 1) {
    throw new ValidationError(
      "The exact replacement connection was not uniquely visible in the current environment; no rebind was attempted.",
    );
  }
  const connection = matches[0]!;
  const replacementConnector = connectorName(connection.properties?.apiId);
  if (
    !replacementConnector ||
    replacementConnector !== connectorName(reference.metadata.connectorid) ||
    !ID_PATTERN.test(connection.name)
  ) {
    throw new ValidationError(
      "The replacement connection's exact connector identity is missing or differs from the existing tool; no rebind was attempted.",
    );
  }
  const statuses = connection.properties?.statuses;
  if (
    !Array.isArray(statuses) ||
    statuses.length === 0 ||
    statuses.length > 100 ||
    statuses.some((status) => status?.status !== "Connected")
  ) {
    throw new ValidationError(
      "The replacement connection does not have exclusively Connected status metadata; no rebind was attempted.",
    );
  }
  const changed = connection.name !== reference.metadata.connectionid;
  const afterReferenceHash = sourceHash(
    JSON.stringify({ ...reference.metadata, connectionid: connection.name }),
  );
  const result = {
    status: parsed.confirm ? "no-change" : "preview",
    environmentId,
    agentId: parsed.agentId,
    componentId: parsed.componentId,
    connector: replacementConnector,
    connectionReferenceId: reference.metadata.connectionreferenceid,
    connectionReferenceLogicalName: tool.reference,
    previousConnectionId: reference.metadata.connectionid,
    connectionId: connection.name,
    connectionMode: "Invoker",
    beforeSourceSha256: beforeSha256,
    expectedSourceSha256: beforeSha256,
    afterSourceSha256: beforeSha256,
    beforeConnectionReferenceSha256: reference.hash,
    expectedConnectionReferenceSha256: reference.hash,
    afterConnectionReferenceSha256: afterReferenceHash,
    changed,
    written: false,
    readbackVerified: false,
    runtimeVerified: false,
    publicationPerformed: false,
    notes:
      "Connected is connection metadata, not endpoint health or runtime authorization. Only the existing Dataverse connectionreference.connectionid changes; the logical name and every YAML byte remain unchanged. The reference record can affect consumers of published and draft versions even though no agent publication is performed. The row ETag condition protects the reference binding only. Source/reference/relationship checks are not a multi-row transaction; visible component consumers are checked, not hidden, provider, workflow or source-text consumers globally. No reference/link creation, authentication, permission, evaluation-binding, publication or execution changes are performed.",
  };
  if (!parsed.confirm || !changed) return JSON.stringify(result, null, 2);

  // The native model keeps the YAML logical name stable and changes the linked row's binding.
  // Microsoft/vscode-copilotstudio f9a34b95, SyncDataverseClient.BindConnectionReferenceAsync.
  const fresh = await readRow(
    api,
    "botcomponents",
    parsed.componentId,
    COMPONENT_SELECT,
    LINK_EXPAND,
  );
  if (sourceHash(checkedSource(fresh, parsed.agentId, parsed.componentId)) !== beforeSha256) {
    throw new ValidationError(
      "The component source changed during verification. Preview again; no rebind was attempted.",
    );
  }
  const freshReference = await readBinding(
    api,
    fresh,
    tool.reference,
    parsed.agentId,
    parsed.componentId,
  );
  if (freshReference.hash !== reference.hash) {
    throw new ValidationError(
      "The connection reference changed during verification. Preview again; no rebind was attempted.",
    );
  }
  const etag = freshReference.etag;
  if (typeof etag !== "string" || !/^W\/"[0-9]{1,20}"$/.test(etag)) {
    throw new ValidationError(
      "A supported fresh Dataverse row ETag is required for a conditional rebind; no write sent.",
    );
  }
  try {
    await api.updateCopilotConnectionReferenceIfUnchanged(
      reference.metadata.connectionreferenceid,
      { connectionid: connection.name },
      etag,
    );
  } catch (error) {
    if (error instanceof CopilotConnectionReferenceUpdateError) throw error;
    throw new CopilotConnectionReferenceUpdateError("transport");
  }
  let saved;
  try {
    saved = await api.getRow("botcomponents", parsed.componentId, COMPONENT_SELECT, LINK_EXPAND);
  } catch {
    throw new ValidationError(
      "The rebind write returned success but readback failed. The accepted reference update is not independently verified; do not automatically retry the write. Inspect the binding. Runtime was not tested.",
    );
  }
  let savedSource: string;
  try {
    savedSource = checkedSource(saved, parsed.agentId, parsed.componentId);
  } catch {
    throw new ValidationError(
      "The rebind write returned success but readback identity, parent, type, or source could not be verified. The accepted update remains unverified; do not automatically retry the write. Inspect the component; runtime was not tested.",
    );
  }
  if (savedSource !== source) {
    throw new ValidationError(
      "The rebind write returned success but the exact source readback differed. Inspect the component; do not blindly retry or roll back over a concurrent edit. Runtime was not tested.",
    );
  }
  try {
    const savedReference = await readBinding(
      api,
      saved,
      tool.reference,
      parsed.agentId,
      parsed.componentId,
    );
    if (savedReference.hash !== afterReferenceHash) throw new Error("mismatch");
  } catch {
    throw new ValidationError(
      "The rebind write returned success but reference binding/consumer readback differed or was unavailable. The accepted update remains unverified; do not automatically retry or roll back. Runtime was not tested.",
    );
  }
  return JSON.stringify(
    { ...result, status: "connection-reference-rebound", written: true, readbackVerified: true },
    null,
    2,
  );
}
